import React from 'react';
import usePage from '@dashboard/hooks/usePage';
import clsx from 'clsx';
import { DragIcon } from '@dashboard/icons/DragIcon';
import Button from '@dashboard/components/Button';
import { formatPrice } from '@dashboard/services/formatting';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import TicketTypeEdit from './TicketTypeEdit';
import { useTicketGroupEdit } from '../TicketGroupEditContext';
import useTicketTypeHelpers from '../useTicketTypeHelpers';
import Tooltip from '@dashboard/components/Tooltip';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import Lozenge from '@dashboard/components/Lozenge';
import { useTicketGroupHelpers } from '../useTicketGroupHelpers';

function checkCanDeleteTicketType(ticketType, page) {
  const hasSoldTickets = ticketType.quantitySold > 0;
  if (hasSoldTickets) {
    return {
      canDeleteTicketType: false,
      canDeleteTicketTypeMessage:
        "You can't delete this ticket type because it has made sales. You can archive it instead.",
    };
  }

  const isLastTicketTypeInPage =
    page.ticketGroups
      .flatMap(tg => tg.ticketTypes)
      .filter(ticketType => ticketType.status !== 'archived').length === 1;

  if (isLastTicketTypeInPage) {
    return {
      canDeleteTicketType: false,
      canDeleteTicketTypeMessage:
        "You can't delete the last ticket type. Add a new ticket type to delete this one.",
    };
  }

  return {
    canDeleteTicketType: true,
    canDeleteTicketTypeMessage: '',
  };
}

function checkCanArchiveTicketType(ticketType, page) {
  const hasSoldTickets = ticketType.quantitySold > 0;
  const isLastTicketTypeInPage =
    page.ticketGroups
      .flatMap(tg => tg.ticketTypes)
      .filter(ticketType => ticketType.status !== 'archived').length === 1;

  if (hasSoldTickets && !isLastTicketTypeInPage) {
    return {
      canArchiveTicketType: true,
    };
  }

  return {
    canArchiveTicketType: false,
  };
}

export default function TicketTypeItem({ dragHandleProps, ticketType, ticketGroupId, onSuccess }) {
  const { page } = usePage();
  const { isAddingTicketType, editingTicketTypeId } = useTicketGroupEdit();
  const {
    onEditTicketType,
    onCancelEditTicketType,
    onDeleteTicketType,
    onEnableDisableTicketType,
    onArchiveTicketType,
    onDuplicateTicketType,
  } = useTicketTypeHelpers(ticketType._id);
  const { isTicketGroupSoldOut } = useTicketGroupHelpers(ticketGroupId);
  const isEditing = editingTicketTypeId === ticketType._id;

  function onEditClick() {
    if (isEditing) {
      onCancelEditTicketType();
    } else {
      onEditTicketType(ticketType._id);
    }
  }

  const { canDeleteTicketType, canDeleteTicketTypeMessage } = checkCanDeleteTicketType(
    ticketType,
    page,
  );
  const { canArchiveTicketType } = checkCanArchiveTicketType(ticketType, page);

  return (
    <div className="w-full">
      <div className={clsx('flex flex-row justify-start z-10 rounded-t')}>
        <div
          {...(!isAddingTicketType && dragHandleProps)}
          className={clsx('pt-2 pr-2', {
            '!cursor-not-allowed': isAddingTicketType,
          })}
        >
          <DragIcon className="w-4 h-4" />
        </div>

        <button
          className={clsx(
            `py-3 px-3 mr-2 bg-white border border-gray-200 truncate w-full text-left flex gap-x-3 justify-between !ring-0 !focus:ring-0 rounded`,
            {
              'hover:border-gray-300 transition-colors': !isAddingTicketType,
              '!border-chocolate-1000': isEditing,
              '!text-gray-900 !hover:text-gray-900 !hover:border-gray-200 cursor-not-allowed':
                isAddingTicketType,
              'opacity-50 pointer-events-none': ticketType.status === 'archived',
            },
          )}
          onClick={onEditClick}
          disabled={isAddingTicketType}
          type="button"
        >
          <div className="flex flex-col items-start justify-start truncate gap-y-2">
            <div className="text-base font-semibold">{ticketType?.name}</div>
            <div className="text-sm text-gray-700">
              {ticketType?.capacity !== null ? `${ticketType.capacity} tickets` : 'Unlimited'}
              {' • '}
              {ticketType?.quantitySold || '0'} sold
            </div>
          </div>

          <div className="flex flex-col items-end justify-end gap-y-1">
            <div className="text-base">
              {ticketType.pricing === 'paid' && ticketType.price > 0
                ? formatPrice({
                    amount: ticketType?.price,
                    currency: page.eventDetails?.currency,
                  })
                : 'Free'}
            </div>

            <div className="flex gap-x-2 ml-2">
              {ticketType.status === 'archived' && (
                <div>
                  <Tooltip>
                    <Lozenge appearance="default">Archived</Lozenge>
                  </Tooltip>
                </div>
              )}

              {ticketType.status === 'disabled' && (
                <div>
                  <Tooltip label="Disabled ticket types will not be shown to customers">
                    <Lozenge appearance="default">Disabled</Lozenge>
                  </Tooltip>
                </div>
              )}

              {ticketType.hidden && (
                <div>
                  <Tooltip label="Hidden ticket types will not be shown to customers">
                    <Lozenge appearance="default">Hidden</Lozenge>
                  </Tooltip>
                </div>
              )}

              {(isTicketGroupSoldOut || ticketType.availabilityStatus === 'sold_out') && (
                <div>
                  <Tooltip label="Sold out">
                    <Lozenge appearance="default">Sold out</Lozenge>
                  </Tooltip>
                </div>
              )}
            </div>
          </div>
        </button>

        <div className="ml-auto">
          <DropdownMenu.Root>
            <DropdownMenu.Trigger
              asChild
              disabled={isAddingTicketType || ticketType.status === 'archived'}
            >
              <Button
                aria-label="more options"
                appearance="icon-button"
                size="narrow"
                disabled={isAddingTicketType || ticketType.status === 'archived'}
              >
                <EllipsisVerticalIcon className="w-4 h-4" />
              </Button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
              <DropdownMenu.Content
                className="origin-top-right absolute right-0 z-20 mt-2 w-40 rounded-md shadow-lg bg-white ring-1 ring-gray-100 focus:outline-none overflow-hidden"
                side="bottom"
                align="end"
              >
                <DropdownMenu.Item
                  onClick={onDuplicateTicketType}
                  className="p-2 outline-none hover:bg-gray-100 cursor-pointer text-gray-900"
                >
                  Duplicate
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  onClick={onEnableDisableTicketType}
                  className="p-2 outline-none hover:bg-gray-100 cursor-pointer text-gray-900"
                >
                  {ticketType.status === 'enabled' ? 'Disable' : 'Enable'}
                </DropdownMenu.Item>

                {canArchiveTicketType && (
                  <DropdownMenu.Item
                    onClick={onArchiveTicketType}
                    className="p-2 outline-none hover:bg-gray-100 cursor-pointer text-gray-900"
                  >
                    Archive
                  </DropdownMenu.Item>
                )}

                {canDeleteTicketType ? (
                  <DropdownMenu.Item
                    onClick={onDeleteTicketType}
                    className="p-2 outline-none hover:bg-gray-100 cursor-pointer text-gray-900"
                  >
                    Delete
                  </DropdownMenu.Item>
                ) : (
                  <RadixTooltip.Provider delayDuration={300}>
                    <RadixTooltip.Root>
                      <DropdownMenu.Item asChild disabled>
                        <RadixTooltip.Trigger className="w-full">
                          <button className="text-left w-full p-2 outline-none hover:bg-gray-100 cursor-pointer text-gray-900 opacity-50 pointer-events-none">
                            Delete
                          </button>
                        </RadixTooltip.Trigger>
                      </DropdownMenu.Item>

                      <RadixTooltip.Content
                        className={clsx(
                          'data-[state=delayed-open]:data-[side=top]:animate-slideDownAndFade data-[state=delayed-open]:data-[side=right]:animate-slideLeftAndFade data-[state=delayed-open]:data-[side=left]:animate-slideRightAndFade data-[state=delayed-open]:data-[side=bottom]:animate-slideUpAndFade select-none leading-none will-change-[transform,opacity]',
                          'inline-flex items-center rounded px-1.5 py-1.5 mx-2',
                          'bg-black dark:bg-gray-800 shadow z-50',
                        )}
                        side="left"
                      >
                        <RadixTooltip.Arrow className="fill-current text-black dark:text-gray-800 mx-auto" />
                        <span className="block text-sm text-white dark:text-gray-100 max-w-xs text-left">
                          {canDeleteTicketTypeMessage}
                        </span>
                      </RadixTooltip.Content>
                    </RadixTooltip.Root>
                  </RadixTooltip.Provider>
                )}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>
      </div>

      {isEditing && (
        <div
          className={clsx(`my-4 pl-6 pr-[26px]`, {
            'opacity-50 pointer-events-none': ticketType.status === 'disabled',
          })}
        >
          <TicketTypeEdit
            ticketType={ticketType}
            ticketGroupId={ticketGroupId}
            onSuccess={onSuccess}
          />
        </div>
      )}
    </div>
  );
}
